import { Injectable } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { Store } from '@ngxs/store';
import {
  BillingAddress,
  isReusablePaymentMethod,
  LicenseType,
  LicenseValidityContext,
  PaymentMethodInfo,
  PricingInformation
} from '@platform-frontend/marketplace';
import { Navigate, ShowInfoNotification, ShowWarningNotification } from '@platform-frontend/util';
import { combineLatest, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AssetState } from '../asset';
import { FlowState } from '../flow';
import {
  MSG_PURCHASE_OR_RENEW_NO_ADDITIONS,
  MSG_PURCHASE_OR_RENEW_NO_PAYMENT_METHOD,
  MSG_PURCHASE_OR_RENEW_NO_PRICING_INFORMATION,
  MSG_PURCHASE_OR_RENEW_NO_QUANTITY,
  MSG_PURCHASE_OR_RENEW_PAGE_LOADING,
  MSG_PURCHASE_OR_RENEW_PRICE_LOADING,
  MSG_RENEW_NO_CONTRACT_ID
} from '../notification-messages';
import { PaymentInformation, WalletPaymentData } from '../payment-wallet';
import { ProductState } from '../product';
import { PurchaseOrRenewAddition } from './model/purchase-or-renew-addition';
import { ShoppingCartBillingState } from './shopping-cart-billing';
import { ShoppingCartPaymentState } from './shopping-cart-payment';
import {
  PersonalizationAddition,
  PersonalizationFirstPersonStatus,
  PersonalizationPerson,
  ShoppingCartPersonalizationState
} from './shopping-cart-personalization';
import { Purchase, PurchaseOrRenew, PurchaseOrRenewActionParams, Renew } from './shopping-cart.actions';
import { ShoppingCartState } from './shopping-cart.state';

@Injectable()
export class ShoppingCartService {
  constructor(private activatedRoute: ActivatedRoute, private store: Store) {}

  tryPurchaseOrRenewAndNavigateTo(successUrl: string, paymentWalletData: WalletPaymentData | null): Observable<unknown> {
    const selectedPaymentMethod: PaymentMethodInfo | null = this.store.selectSnapshot(ShoppingCartPaymentState.selectedPaymentMethod);
    const paymentInformation: PaymentInformation | null = selectedPaymentMethod
      ? isReusablePaymentMethod(selectedPaymentMethod)
        ? selectedPaymentMethod
        : paymentWalletData
      : null;
    const isRenewal = this.store.selectSnapshot(FlowState.isOrderToBeManaged) && this.store.selectSnapshot(AssetState.subscriptionBookingStatus) === 'EXPIRING';
    return this.dispatchPurchaseOrRenewAction(
      this.buildPurchaseOrRenewAdditions(),
      this.store.selectSnapshot(ShoppingCartState.selectedLicenseType),
      paymentInformation,
      this.store.selectSnapshot(ShoppingCartBillingState.selectedBillingAddress),
      this.store.selectSnapshot(ShoppingCartBillingState.selectedCountry),
      this.store.selectSnapshot(ShoppingCartState.pricingInformation),
      successUrl,
      isRenewal,
      this.store.selectSnapshot(ShoppingCartState.selectedQuantity),
      this.store.selectSnapshot(AssetState.contractId),
      this.store.selectSnapshot(isRenewal ? AssetState.licenseValidity : ShoppingCartState.selectedValidity)
    );
  }

  private dispatchPurchaseOrRenewAction(
    additions: PurchaseOrRenewAddition[] | null,
    licenseType: LicenseType | null,
    paymentInformation: PaymentInformation | null,
    billingAddress: BillingAddress | null,
    country: string,
    pricingInformation: PricingInformation | 'LOADING' | null,
    successUrl: string,
    isRenewal: boolean,
    quantity: number | null,
    contractId: string | null,
    validity: LicenseValidityContext | null
  ): Observable<unknown> {
    if (paymentInformation === null) {
      return this.store.dispatch(new ShowWarningNotification(MSG_PURCHASE_OR_RENEW_NO_PAYMENT_METHOD));
    } else if (licenseType === null) {
      return this.store.dispatch(new ShowInfoNotification(MSG_PURCHASE_OR_RENEW_PAGE_LOADING));
    } else if (pricingInformation === 'LOADING') {
      return this.store.dispatch(new ShowInfoNotification(MSG_PURCHASE_OR_RENEW_PRICE_LOADING));
    } else if (pricingInformation === null) {
      return this.store.dispatch(new ShowWarningNotification(MSG_PURCHASE_OR_RENEW_NO_PRICING_INFORMATION));
    } else if (quantity === null) {
      return this.store.dispatch(new ShowWarningNotification(MSG_PURCHASE_OR_RENEW_NO_QUANTITY));
    } else if (additions === null) {
      return this.store.dispatch(new ShowWarningNotification(MSG_PURCHASE_OR_RENEW_NO_ADDITIONS));
    } else {
      let purchaseOrRenew: PurchaseOrRenew;
      const purchaseOrRenewActionParams: PurchaseOrRenewActionParams = {
        additions,
        licenseType,
        paymentInformation,
        billingAddress,
        country,
        pricingInformation,
        quantity,
        validity
      };
      if (isRenewal) {
        if (contractId === null) {
          return this.store.dispatch(new ShowWarningNotification(MSG_RENEW_NO_CONTRACT_ID));
        }
        purchaseOrRenew = new Renew(purchaseOrRenewActionParams, contractId);
      } else {
        purchaseOrRenew = new Purchase(purchaseOrRenewActionParams);
      }

      const queryParams: Params = { reason: isRenewal ? 'renew' : 'purchase' };
      return this.store
        .dispatch(purchaseOrRenew)
        .pipe(switchMap(() => this.store.dispatch(new Navigate([successUrl], { relativeTo: this.activatedRoute, queryParams: queryParams }))));
    }
  }

  private buildPurchaseOrRenewAdditions(): PurchaseOrRenewAddition[] | null {
    const additions: PurchaseOrRenewAddition[] = [];
    if (this.store.selectSnapshot(ProductState.personalizationAddition)) {
      const personalizationAddition = this.buildPersonalizationAddition();
      if (personalizationAddition === null) return null;
      additions.push(personalizationAddition);
    }
    return additions;
  }

  private buildPersonalizationAddition(): PersonalizationAddition | null {
    const firstPerson = this.store.selectSnapshot(ShoppingCartPersonalizationState.firstPerson);
    const additionalPersons = this.store.selectSnapshot(ShoppingCartPersonalizationState.additionalPersons);
    const birthDateRequirement = this.store.selectSnapshot(ProductState.birthDateRequirement);

    const persons: PersonalizationPerson[] = [];

    if (firstPerson && this.isFirstPersonComplete(firstPerson, birthDateRequirement !== 'NONE')) {
      persons.push(firstPerson);
    } else {
      return null;
    }

    additionalPersons.forEach(person => {
      persons.push({ ...person, isUser: false });
    });

    return { type: 'PERSONALIZATION', persons };
  }

  getPersonalizationAdditionStatus(): Observable<PersonalizationFirstPersonStatus> {
    const firstPerson$ = this.store.select(ShoppingCartPersonalizationState.firstPerson);
    const arePersonsSet$ = this.store.select(ShoppingCartPersonalizationState.arePersonsSet);
    const birthDateRequirement$ = this.store.select(ProductState.birthDateRequirement);

    return combineLatest([firstPerson$, arePersonsSet$, birthDateRequirement$]).pipe(
      map(([firstPerson, arePersonsSet, birthDateRequirement]) =>
        firstPerson === null && !arePersonsSet
          ? 'NOT_SET'
          : this.isFirstPersonComplete(firstPerson, birthDateRequirement !== 'NONE')
          ? 'COMPLETE'
          : 'INCOMPLETE'
      )
    );
  }

  private isFirstPersonComplete = (
    firstPerson: PersonalizationPerson<string | null> | null,
    birthDateRequired: boolean
  ): firstPerson is PersonalizationPerson<string> =>
    firstPerson !== null && firstPerson.firstName !== null && firstPerson.lastName !== null && (!birthDateRequired || firstPerson.birthDate !== null);
}
